import { useState, useEffect, useRef, type ReactNode } from "react";
import { Clock, Copy, FileText, Loader2, Users, Zap } from "lucide-react";
import { CardErrorBadge } from "@/components/ui/CardErrorBadge";
import { getStudyTypeMeta } from "@/features/studies/studyTypeMeta";
import { cn } from "@/lib/utils";
import type {
  StudiesLobbyDraftItem,
  StudiesLobbyExperimentItem,
  StudiesLobbyItem,
  Test,
} from "@/types/routes/tests";
import { getDraftDisplayCopy } from "../../draftDisplay";
import { resolveIncompleteTwinDelivery } from "@/features/studies/incompleteTwinDelivery";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";
import { TestRowActions } from "./TestRowActions";
import {
  STUDY_LIST_TABLE_ROW_CLASS,
} from "../studyListTableLayout";

const STUDY_CARD_DELETE_ACTION_SLOT_CLASS =
  "absolute top-3 right-3 z-20 opacity-0 transition-opacity duration-200 group-hover:opacity-100";

const STUDY_CARD_MENU_ACTION_SLOT_CLASS =
  "absolute top-3 right-3 z-20 rounded-[6px] border border-white/20 bg-black/35 shadow-sm backdrop-blur-md md:opacity-0 md:transition-opacity md:duration-150 md:group-hover:opacity-100 [&:has([data-state=open])]:opacity-100";

function StudyCardOverlayActionSlot({
  deleteOnly,
  children,
}: {
  deleteOnly: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={deleteOnly ? STUDY_CARD_DELETE_ACTION_SLOT_CLASS : STUDY_CARD_MENU_ACTION_SLOT_CLASS}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

/**
 * Fades in once the image is fully decoded and ready to paint.
 * Handles browser-cached images (where onLoad fires synchronously before
 * the component mounts) by checking `img.complete` via a ref after mount.
 */
function CoverImage({
  src,
  className,
}: {
  src: string;
  className?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // For cached images the browser marks `complete` before React attaches onLoad.
    if (imgRef.current?.complete) {
      setLoaded(true);
    }
  }, []);

  return (
    <img
      ref={imgRef}
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      className={cn(
        "h-full w-full object-cover transition-opacity duration-500",
        loaded ? "opacity-100" : "opacity-0",
        className,
      )}
      onLoad={() => setLoaded(true)}
    />
  );
}

export interface StudyLobbyCardProps {
  variant: "grid" | "list";
  item: StudiesLobbyItem;
  studyName: string;
  updatedLabel: string;
  navigationDisabled: boolean;
  isPlatformAdmin: boolean;
  currentUserId?: string;
  onOpen: () => void;
  onRequestDelete: () => void;
  onDuplicate: () => void;
}

function StudyTypeBadge({ goalId }: { goalId?: string | null }) {
  const meta = getStudyTypeMeta(goalId);
  return <span data-testid="study-type-badge" className={cn("inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium", meta.className)}>{meta.label}</span>;
}

function CreatorCell({
  test,
  currentUserId,
}: {
  test: Test;
  currentUserId?: string;
}) {
  const isYou = currentUserId && test.userId === currentUserId;
  const name = test.creatorName;

  if (isYou) {
    return <span className="text-base text-muted-foreground font-sans">{i18n.t("studyLobby.you", { ns: "app" })}</span>;
  }

  if (!name) {
    return <span className="text-base text-muted-foreground font-sans">—</span>;
  }

  return <span className="truncate text-base text-muted-foreground font-sans">{name}</span>;
}

/**
 * Figma grid: top-left type row — clock (in progress), red alert (failed),
 * or no icon (completed). StudyType pill always when known.
 */
function GridImageStatusRow({
  test,
}: {
  test: Test;
  navigationDisabled: boolean;
}) {
  const s = test.status;
  const isFailed = s === "failed" || s === "cancelled";
  const isRunning = s === "running";
  const isPending = s === "pending";
  const isReportGenerating =
    s === "completed" &&
    test.reportStatus !== "ready" &&
    test.reportStatus !== "failed";
  const inProgress = isRunning || isPending || isReportGenerating;
  const shared = test.isSharedWithMe === true;

  const statusTitle = isReportGenerating
    ? i18n.t("studyLobby.preparingReport", { ns: "app" })
    : isRunning
      ? i18n.t("studyLobby.running", { ns: "app" })
      : i18n.t("studyLobby.pending", { ns: "app" });

  return (
    <div className="pointer-events-none absolute left-3 top-3 z-[1] flex max-w-[calc(100%-3rem)] flex-wrap items-center gap-1.5">
      {isFailed && (
        <CardErrorBadge dataTestId="study-grid-error-badge" />
      )}
      {inProgress && !isFailed && (
        <span
          className="flex size-[22px] shrink-0 items-center justify-center rounded-full border border-white/25 bg-white/80 shadow-sm backdrop-blur-sm"
          title={statusTitle}
          aria-label={statusTitle}
        >
          {isReportGenerating || isRunning ? (
            <Loader2 className="size-3 animate-spin text-foreground" />
          ) : (
            <Clock className="size-3 text-foreground" />
          )}
        </span>
      )}
      {shared && (
        <span className="inline-flex items-center gap-0.5 rounded-full border border-white/30 bg-white/15 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
          <Users className="size-2.5 shrink-0" aria-hidden />
          {i18n.t("studyLobby.shared", { ns: "app" })}
        </span>
      )}
      {test.studyMode === 'quick' && (
        <span
          className="inline-flex items-center gap-0.5 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary backdrop-blur-sm"
          data-testid="quick-study-badge"
        >
          <Zap className="size-2.5 shrink-0" aria-hidden />
          {i18n.t("studyLobby.quick", { ns: "app" })}
        </span>
      )}
    </div>
  );
}

/**
 * Draft cards use the same selected lobby layout as launched studies.
 */
function DraftLobbyCard({
  variant,
  draft,
  studyName,
  updatedLabel,
  isPlatformAdmin,
  onOpen,
  onRequestDelete,
  onDuplicate,
}: {
  variant: "grid" | "list";
  draft: StudiesLobbyDraftItem;
  studyName: string;
  updatedLabel: string;
  isPlatformAdmin: boolean;
  onOpen: () => void;
  onRequestDelete: () => void;
  onDuplicate: () => void;
}) {
  const { t } = useTranslation(["app", "common"]);
  const { statusLabel, actionLabel } = getDraftDisplayCopy(draft.status);
  const isDuplicating = draft.status === "duplicating";
  const isDuplicateFailed = draft.status === "duplicate_failed";
  // Both duplicating and duplicate_failed cards are non-navigable. The nav
  // surface is blocked; the actions menu still shows Delete for cleanup.
  const isProcessing = isDuplicating || isDuplicateFailed;

  const actions = (
    <TestRowActions
      isPlatformAdmin={isPlatformAdmin}
      isGlobal={false}
      onView={onOpen}
      onRequestDelete={onRequestDelete}
      onDuplicate={isProcessing ? undefined : onDuplicate}
      viewLabel={actionLabel}
      deleteLabel={t("delete", { ns: "common" })}
      deleteOnly={isProcessing}
      actionSurface="row"
      inlineConfirm
      deleteConfirmMessage={t("studyLobby.deleteDraftQ")}
    />
  );

  const listTestId = isDuplicating
    ? "draft-lobby-card-duplicating"
    : isDuplicateFailed
      ? "draft-lobby-card-failed"
      : "draft-lobby-card-surface";

  if (variant === "grid") {
    return (
      <div className="group relative overflow-hidden rounded-xl bg-card shadow-xl">
        <button
          type="button"
          data-testid={listTestId}
          className={cn("relative flex h-[390px] w-full flex-col justify-between bg-gradient-to-br from-muted via-background to-muted p-4 text-left", isProcessing ? "cursor-not-allowed opacity-70" : "cursor-pointer")}
          onClick={isProcessing ? undefined : onOpen}
          disabled={isProcessing}
        >
          <div className="flex items-start gap-2">
            <span className="rounded-full border border-border/70 bg-background/85 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{statusLabel}</span>
          </div>
          <div>
            <div className="mb-2">
              <StudyTypeBadge goalId={draft.goalId} />
            </div>
            <h3 className="line-clamp-3 text-[22px] font-display font-normal leading-[1.25] text-foreground">{studyName}</h3>
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <span>{t("studyLobby.you")}</span><span>·</span><span>{t("studyLobby.lastEdited", { when: updatedLabel })}</span>
            </div>
          </div>
        </button>
        <StudyCardOverlayActionSlot deleteOnly={isProcessing}>{actions}</StudyCardOverlayActionSlot>
      </div>
    );
  }

  return (
      <div
        className={cn(
          STUDY_LIST_TABLE_ROW_CLASS,
          "group",
          isProcessing ? "opacity-70" : "hover:bg-muted/40",
        )}
        {...(isProcessing ? { "data-testid": listTestId } : {})}
      >
        <button
          type="button"
          {...(!isProcessing ? { "data-testid": listTestId } : {})}
          className={cn(
            "flex min-w-0 items-center gap-3 text-left",
            isProcessing
              ? "cursor-not-allowed pointer-events-none"
              : "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          )}
          onClick={isProcessing ? undefined : onOpen}
          onKeyDown={(e) => {
            if (isProcessing) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen();
            }
          }}
          aria-label={`${actionLabel}: ${studyName}`}
          aria-disabled={isProcessing}
          disabled={isProcessing}
        >
          <div className="relative flex h-[58px] w-[80px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-muted dark:shadow-[0px_1px_7px_0px_rgba(7,18,15,0.25)]">
            {isDuplicating ? (
              <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
            ) : isDuplicateFailed ? (
              <Copy className="size-5 text-destructive" aria-hidden />
            ) : (
              <FileText className="size-5 text-muted-foreground" aria-hidden />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-sans font-medium leading-5 text-foreground">
              {studyName}
            </h3>
            {(isDuplicating || isDuplicateFailed || statusLabel !== t("studies.draftStatus.draft")) && (
              <p
                className={cn(
                  "mt-0.5 font-sans",
                  isDuplicating || isDuplicateFailed ? "text-base" : "text-xs",
                  isDuplicateFailed ? "text-destructive" : "text-muted-foreground",
                )}
              >
                {statusLabel}
              </p>
            )}
          </div>
        </button>

        <div>
          <StudyTypeBadge goalId={draft.goalId} />
        </div>

        <div className="text-base text-muted-foreground font-sans">—</div>

        <div className="text-base text-muted-foreground font-sans">{t("studyLobby.you")}</div>

        <div className="text-base text-muted-foreground font-sans">
          {updatedLabel}
        </div>

        <div
          className={cn(
            "justify-self-end transition-opacity duration-200",
            isProcessing
              ? "w-8 opacity-0 group-hover:opacity-100 [&:has([data-state=open])]:opacity-100"
              : "md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 md:[&:has([data-state=open])]:opacity-100",
          )}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {actions}
        </div>
      </div>
  );
}

export function StudyLobbyCard({
  variant,
  item,
  studyName,
  updatedLabel,
  navigationDisabled,
  isPlatformAdmin,
  currentUserId,
  onOpen,
  onRequestDelete,
  onDuplicate,
}: StudyLobbyCardProps) {
  const { t } = useTranslation(["app", "common"]);

  if (item.itemKind === "draft") {
    return (
      <DraftLobbyCard
        variant={variant}
        draft={item}
        studyName={studyName}
        updatedLabel={updatedLabel}
        isPlatformAdmin={isPlatformAdmin}
        onOpen={onOpen}
        onRequestDelete={onRequestDelete}
        onDuplicate={onDuplicate}
      />
    );
  }

  const test: StudiesLobbyExperimentItem = item;
  const twinShortfall = resolveIncompleteTwinDelivery({
    completed: test.respondentsCompleted,
    requested: test.numRespondents,
    status: test.status,
  });
  const respondentLabel = twinShortfall
    ? t("studyReport.incompleteTwins", {
        completed: twinShortfall.completed,
        requested: twinShortfall.requested,
      })
    : null;

  const actions = (
    <TestRowActions
      isPlatformAdmin={isPlatformAdmin}
      isGlobal={test.isGlobal}
      onView={onOpen}
      onRequestDelete={onRequestDelete}
      onDuplicate={navigationDisabled ? undefined : onDuplicate}
      deleteOnly={navigationDisabled}
      actionSurface={variant === "grid" ? "overlay" : "row"}
    />
  );

  if (variant === "list") {
    const isFailed = test.status === "failed" || test.status === "cancelled";
    const isRunning = test.status === "running";
    const isPending = test.status === "pending";
    const reportHeadline = test.reportHeadline?.trim() ?? "";
    const isCompletedWithInsight =
      test.status === "completed" &&
      test.reportStatus === "ready" &&
      reportHeadline.length > 0;
    const isReportGenerating =
      test.status === "completed" &&
      test.reportStatus !== "ready" &&
      test.reportStatus !== "failed";

    return (
      <div
        data-testid="study-lobby-card-surface"
        className={cn(
          STUDY_LIST_TABLE_ROW_CLASS,
          "group relative",
          navigationDisabled ? "cursor-default" : "hover:bg-muted/40",
        )}
      >
        <button
          type="button"
          className={cn(
            "flex min-w-0 items-center gap-3 text-left",
            navigationDisabled && !isFailed && "opacity-80",
            navigationDisabled
              ? "cursor-not-allowed"
              : "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          )}
          onClick={() => {
            if (!navigationDisabled) onOpen();
          }}
          onKeyDown={(e) => {
            if (navigationDisabled) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen();
            }
          }}
          aria-label={
            navigationDisabled
              ? t("studyLobby.failedStudyAria", { name: studyName })
              : isReportGenerating
                ? t("studyLobby.preparingResultsFor", { name: studyName })
                : t("studyLobby.viewResultsFor", { name: studyName })
          }
          aria-disabled={navigationDisabled}
          disabled={navigationDisabled}
        >
            <div
              className={cn(
                "relative h-[58px] w-[80px] shrink-0 overflow-hidden rounded-[6px] bg-muted dark:shadow-[0px_1px_7px_0px_rgba(7,18,15,0.25)]",
                navigationDisabled && isFailed && "flex items-center justify-center",
              )}
            >
              {navigationDisabled && isFailed ? (
                <>
                  {test.coverImageUrl ? (
                    <>
                      <CoverImage
                        src={test.coverImageUrl}
                        className="grayscale opacity-40"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-muted/30">
                        <CardErrorBadge dataTestId="study-error-badge" />
                      </div>
                    </>
                  ) : (
                    <CardErrorBadge dataTestId="study-error-badge" />
                  )}
                </>
              ) : (
                <>
                  {test.coverImageUrl ? (
                    <CoverImage src={test.coverImageUrl} />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-muted to-muted/60" />
                  )}
                  {isRunning && (
                    <div className="absolute bottom-1 right-1 flex size-[18px] items-center justify-center rounded-full border border-white/25 bg-white/80 shadow-sm backdrop-blur-[4px]">
                      <Loader2 className="size-2.5 animate-spin text-[hsl(var(--platform-deep))]" />
                    </div>
                  )}
                  {isPending && (
                    <div className="absolute bottom-1 right-1 flex size-[18px] items-center justify-center rounded-full border border-white/25 bg-white/80 shadow-sm backdrop-blur-[4px]">
                      <Clock className="size-2.5 text-[hsl(var(--platform-deep))]" />
                    </div>
                  )}
                  {isReportGenerating && (
                    <div
                      className="absolute bottom-1 right-1 flex size-[18px] items-center justify-center rounded-full border border-white/25 bg-white/80 shadow-sm backdrop-blur-[4px]"
                      title={t("studyLobby.preparingReport")}
                    >
                      <Loader2 className="size-2.5 animate-spin text-[hsl(var(--platform-deep))]" />
                    </div>
                  )}
                </>
              )}
            </div>

          <div className="min-w-0 flex-1">
            <h3
              className={cn(
                "line-clamp-2 text-sm font-sans leading-5 text-foreground",
                navigationDisabled && "text-foreground/70",
              )}
            >
              <span className="font-medium">
                {isCompletedWithInsight ? reportHeadline : studyName}
              </span>
              {isCompletedWithInsight && (
                <span className="font-normal text-muted-foreground">
                  {' — '}{studyName}
                </span>
              )}
            </h3>
              {test.isSharedWithMe && (
                <span className="mt-0.5 inline-flex items-center gap-0.5 text-[12px] font-medium text-muted-foreground">
                  <Users className="size-2.5 shrink-0" aria-hidden />
                  {t("studyLobby.shared")}
                </span>
              )}
              {test.studyMode === 'quick' && (
                <span
                  className="mt-0.5 inline-flex items-center gap-0.5 text-[12px] font-medium text-primary"
                  data-testid="quick-study-badge"
                >
                  <Zap className="size-2.5 shrink-0" aria-hidden />
                  {t("studyLobby.quickStudy")}
                </span>
              )}
            </div>
          </button>

        <div>
          <StudyTypeBadge goalId={test.goalId} />
        </div>

        <div
          className="text-base text-muted-foreground font-sans"
          data-testid="study-lobby-respondent-count"
        >
          {respondentLabel ??
            t("studyLobby.respondents", { count: test.numRespondents })}
        </div>

        <div>
          <CreatorCell test={test} currentUserId={currentUserId} />
        </div>

        <div className="text-base text-muted-foreground font-sans">
          {updatedLabel}
        </div>

        <div
          className={cn(
            "justify-self-end transition-opacity duration-200",
            navigationDisabled
              ? "opacity-0 group-hover:opacity-100"
              : "md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100",
          )}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {actions}
        </div>
      </div>
    );
  }

  /* grid — visual-first card with image overlays */
  const isFailed = test.status === "failed" || test.status === "cancelled";
  const reportHeadline = test.reportHeadline?.trim() ?? "";
  const isCompletedWithInsight =
    test.status === "completed" &&
    test.reportStatus === "ready" &&
    reportHeadline.length > 0;

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-xl border-0 shadow-xl transition-all",
        !navigationDisabled && "bg-card/40 hover:-translate-y-0.5",
        navigationDisabled && "bg-muted shadow-none",
      )}
    >
      <div
        data-testid="study-lobby-card-surface"
        role={navigationDisabled ? undefined : "button"}
        tabIndex={navigationDisabled ? undefined : 0}
        className={cn(
          "relative text-left",
          navigationDisabled
            ? "cursor-default pointer-events-none"
            : "cursor-pointer",
        )}
        onClick={() => {
          if (!navigationDisabled) onOpen();
        }}
        onKeyDown={(e) => {
          if (navigationDisabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        aria-label={
          navigationDisabled
            ? t("studyLobby.failedStudyAria", { name: studyName })
            : test.status === "completed" &&
              test.reportStatus !== "ready" &&
              test.reportStatus !== "failed"
            ? t("studyLobby.preparingResultsFor", { name: studyName })
            : t("studyLobby.viewResultsFor", { name: studyName })
        }
        aria-disabled={navigationDisabled}
      >
        <div
          className={cn(
            "relative w-full bg-muted h-[420px] lg:h-[390px]",
            navigationDisabled && "pointer-events-none",
          )}
        >
          {navigationDisabled && isFailed ? (
            test.coverImageUrl ? (
              <CoverImage
                src={test.coverImageUrl}
                className="grayscale opacity-40"
              />
            ) : null
          ) : (
            <>
              {test.coverImageUrl ? (
                <CoverImage src={test.coverImageUrl} />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-muted via-muted/80 to-background" />
              )}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
            </>
          )}

          <GridImageStatusRow
            test={test}
            navigationDisabled={navigationDisabled}
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 px-3 pb-4 pt-16">
            <div className="mb-2">
              <StudyTypeBadge goalId={test.goalId} />
            </div>
            <h3
              className={cn(
                "line-clamp-5 max-h-[6.75em] overflow-hidden text-[20px] font-display font-normal leading-[1.35] pb-0.5",
                navigationDisabled && isFailed
                  ? "text-foreground/70"
                  : "text-white",
              )}
            >
              {isCompletedWithInsight ? reportHeadline : studyName}
            </h3>
            {isCompletedWithInsight && (
              <p className={cn("mt-1 truncate text-sm", navigationDisabled && isFailed ? "text-foreground/55" : "text-white/75")}>{studyName}</p>
            )}
            <div
              className={cn(
                "mt-2 flex items-center gap-2 text-[14px]",
                navigationDisabled && isFailed
                  ? "text-muted-foreground"
                  : "text-white/90",
              )}
            >
              <span data-testid="study-lobby-respondent-count">
                {respondentLabel ??
                  t("studyLobby.respondentsShort", { count: test.numRespondents })}
              </span>
              <span
                className={cn(
                  navigationDisabled && isFailed
                    ? "text-muted-foreground/50"
                    : "text-white/50",
                )}
              >
                ·
              </span>
              <span>{updatedLabel}</span>
              {test.creatorName && (
                <>
                  <span className={cn(navigationDisabled && isFailed ? "text-muted-foreground/50" : "text-white/50")}>·</span>
                  <span>{currentUserId && test.userId === currentUserId ? t("studyLobby.you") : test.creatorName}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <StudyCardOverlayActionSlot deleteOnly={navigationDisabled}>
        {actions}
      </StudyCardOverlayActionSlot>
    </div>
  );
}
